import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './header.style.js';
import { authService, AuthUser } from '../../services/auth.service.js';
import { SubscriptionPlanCat, SUBSCRIPTION_PLAN_TEXT } from '@cmms/models';

@customElement('cmms-header')
export class CMMSHeader extends LitElement {
  static styles = styles;

  @property({ type: String })
  pageTitle = '';

  @property({ type: Boolean })
  showSidebarToggle = false;

  @property({ type: Boolean })
  isSettingsPage = false;

  @property({ type: String })
  companyId = '';

  @property({ type: Number })
  subscriptionPlan: SubscriptionPlanCat = SubscriptionPlanCat.STARTER;

  @state()
  private user: AuthUser | null = null;


  private unsubscribeAuth?: () => void;

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for auth state changes
    this.unsubscribeAuth = authService.onAuthStateChanged((user) => {
      this.user = user;
    });
  }


  private handleSidebarToggle() {
    if (this.isSettingsPage) {
      // Navigate back to dashboard
      this.dispatchEvent(new CustomEvent('navigate', {
        detail: { page: 'dashboard' },
        bubbles: true,
        composed: true
      }));
    } else {
      // Toggle sidebar
      this.dispatchEvent(new CustomEvent('sidebar-toggle', {
        bubbles: true,
        composed: true
      }));
    }
  }

  private handleLogout() {
    authService.signOut();
  }

  private handleProfileClick() {
    this.dispatchEvent(new CustomEvent('profile-requested', {
      bubbles: true,
      composed: true
    }));
  }

  private handleLogoClick() {
    this.dispatchEvent(new CustomEvent('navigate', {
      detail: { page: 'dashboard' },
      bubbles: true,
      composed: true
    }));
  }

  private getUserDisplayName(): string {
    if (this.user?.displayName) {
      return this.user.displayName;
    }
    if (this.user?.email) {
      // Extract name from email (part before @)
      return this.user.email.split('@')[0];
    }
    return 'User';
  }

  private isAdminOrSuperAdmin(): boolean {
    return this.user?.role === 'admin' || this.user?.role === 'super admin';
  }

  private getSubscriptionPlanText(): string {
    return SUBSCRIPTION_PLAN_TEXT[this.subscriptionPlan] || 'Unknown';
  }

  private getSubscriptionPlanVariant(): 'primary' | 'success' | 'warning' {
    switch (this.subscriptionPlan) {
      case SubscriptionPlanCat.STARTER:
        return 'warning';
      case SubscriptionPlanCat.PROFESSIONAL:
        return 'primary';
      case SubscriptionPlanCat.ENTERPRISE:
        return 'success';
      default:
        return 'primary';
    }
  }


  render() {
    return html`
      <header class="header">
        <div class="header-left">
          ${this.showSidebarToggle ? html`
            <sl-icon-button
              name="${this.isSettingsPage ? 'arrow-left' : 'list'}"
              size="large"
              @click=${this.handleSidebarToggle}
              title="${this.isSettingsPage ? 'Back to Dashboard' : 'Toggle Menu'}"
            ></sl-icon-button>
          ` : ''}
          
          ${this.pageTitle ? html`
            <div class="page-title">
              <h2>${this.pageTitle}</h2>
            </div>
          ` : ''}
        </div>

        <div class="header-center">
          <div class="logo" @click=${this.handleLogoClick}>
            <img 
              src="https://firebasestorage.googleapis.com/v0/b/work-order-managment.firebasestorage.app/o/pristinemaint.png?alt=media&token=2dd117e4-d4e7-4bd6-b666-548c5068b566" 
              alt="PristineMaint Logo" 
              class="logo-image"
            >
            <h1 class="logo-text">PristineMaint</h1>
          </div>
        </div>

        <div class="header-right">
          ${this.user ? html`
            ${this.isAdminOrSuperAdmin() ? html`
              <sl-badge 
                variant="${this.getSubscriptionPlanVariant()}"
                class="subscription-badge"
                title="Current Subscription Plan"
              >
                ${this.getSubscriptionPlanText()}
              </sl-badge>
            ` : ''}
            
            <div class="user-info">
              <span class="user-name">${this.getUserDisplayName()}</span>
              <sl-icon-button 
                name="gear"
                size="large"
                @click=${this.handleProfileClick}
                title="Settings"

              >

              </sl-icon-button>
            </div>
            <sl-icon-button 
              name="box-arrow-right"
              size="large"
              @click=${this.handleLogout}
            >
              
            </sl-icon-button>
          ` : ''}
        </div>
      </header>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cmms-header': CMMSHeader;
  }
}
